/** Small, progressively enhanced product previews; no product API or quick-add dependency. */
class TestimonialHotspots extends HTMLElement {
  connectedCallback() {
    this.pointer = null;
    this.trigger = null;
    this.dialog = this.querySelector('.bm-testimonial-product-dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.querySelectorAll('[data-testimonial-hotspot]').forEach((link) => link.setAttribute('aria-haspopup', 'dialog'));
    this.addEventListener('pointerdown', (event) => {
      const trigger = event.target.closest('[data-testimonial-hotspot]');
      this.pointer = trigger ? { trigger, id: event.pointerId, x: event.clientX, y: event.clientY, moved: false } : null;
    }, options);
    this.addEventListener('pointermove', (event) => {
      if (this.pointer?.id === event.pointerId && Math.hypot(event.clientX - this.pointer.x, event.clientY - this.pointer.y) > 8) this.pointer.moved = true;
    }, { ...options, passive: true });
    this.addEventListener('pointercancel', () => { if (this.pointer) this.pointer.moved = true; }, options);
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-testimonial-hotspot]');
      if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      if (event.detail !== 0 && this.pointer?.trigger === trigger && this.pointer.moved) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const template = this.querySelector(`#${CSS.escape(trigger.dataset.testimonialHotspot)}`);
      if (!template) return;
      event.preventDefault();
      this.open(trigger, template);
    }, { ...options, capture: true });
    this.dialog.querySelector('button').addEventListener('click', () => this.close(), options);
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const bounds = this.dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
    }, options);
    this.dialog.addEventListener('close', () => {
      if (this.dialog.open) return;
      this.trigger?.removeAttribute('aria-expanded');
      if (this.trigger?.isConnected && !document.querySelector('.bm-testimonial-product-dialog[open]')) this.trigger.focus({ preventScroll: true });
      this.trigger = null;
      this.dialog.querySelector('.bm-testimonial-product-dialog__content').replaceChildren();
    }, options);
    const schedulePosition = () => {
      if (!this.dialog.open || this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.position(); });
    };
    window.addEventListener('resize', schedulePosition, options);
    // Background scroll (including focus/layout adjustments) is not dismissal.
    // Keep the desktop card anchored without reacting to unrelated app scrollers.
    window.addEventListener('scroll', (event) => {
      if (event.target === document || (event.target instanceof Element && event.target.contains(this.trigger))) schedulePosition();
    }, { ...options, capture: true, passive: true });
    this.closest('.shopify-section')?.addEventListener('shopify:block:select', () => this.close(), options);
    this.addEventListener('shopify:block:select', () => this.close(), options);
  }

  open(trigger, template) {
    this.pointer = null;
    document.querySelectorAll('bm-testimonial-hotspots').forEach((other) => { if (other !== this) other.close?.(); });
    this.trigger?.removeAttribute('aria-expanded');
    this.trigger = trigger;
    this.dialog.querySelector('.bm-testimonial-product-dialog__content').replaceChildren(template.content.cloneNode(true));
    this.dialog.setAttribute('aria-label', template.content.querySelector('h3').textContent);
    trigger.setAttribute('aria-expanded', 'true');
    if (!this.dialog.open) this.dialog.showModal();
    this.position();
    this.dialog.querySelector('button').focus({ preventScroll: true });
  }

  position() {
    if (!this.dialog.open || !this.trigger || matchMedia('(max-width: 749px)').matches) return;
    const anchor = this.trigger.getBoundingClientRect();
    const card = this.dialog.getBoundingClientRect();
    const left = anchor.right + 8 + card.width <= innerWidth - 12 ? anchor.right + 8 : anchor.left - card.width - 8;
    this.dialog.style.left = `${Math.max(12, Math.min(left, innerWidth - card.width - 12))}px`;
    this.dialog.style.top = `${Math.max(12, Math.min(anchor.top, innerHeight - card.height - 12))}px`;
  }

  close() { if (this.dialog?.open) this.dialog.close(); }

  disconnectedCallback() {
    this.events?.abort();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.close();
    this.trigger?.removeAttribute('aria-expanded');
    this.trigger = null;
    this.pointer = null;
  }
}
if (!customElements.get('bm-testimonial-hotspots')) customElements.define('bm-testimonial-hotspots', TestimonialHotspots);
